import type { FC, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type FieldState = "default" | "error";

/**
 * How every dropdown panel sizes itself: `w-max` lets the longest option set the width, so a label
 * the field is too narrow to show is read in full rather than clipped. It is never narrower than
 * the field it hangs off, and never wider than 560 px or the viewport — past that labels wrap.
 *
 * Two flavours because the trigger width arrives under a different custom property depending on
 * which Radix primitive draws the field.
 */
const PANEL_WIDTH = "w-max max-w-[min(560px,calc(100vw-32px))]";

export const POPOVER_PANEL_WIDTH = `${PANEL_WIDTH} min-w-(--radix-popover-trigger-width)`;

export const SELECT_PANEL_WIDTH = `${PANEL_WIDTH} min-w-(--radix-select-trigger-width)`;

type FieldLabelProps = {
  htmlFor?: string;
  required?: boolean;
  state?: FieldState;
  children?: ReactNode;
};

export const FieldLabel: FC<FieldLabelProps> = ({ htmlFor, required, state = "default", children }) => {
  if (!children) return null;

  return (
    <label htmlFor={htmlFor} className={cn("text-16", state === "error" ? "text-brand-red" : "text-neutral-black")}>
      {children}
      {required && <span className="text-brand-red"> *</span>}
    </label>
  );
};

type FieldMessageProps = {
  state?: FieldState;
  children?: ReactNode;
};

export const FieldMessage: FC<FieldMessageProps> = ({ state = "default", children }) => {
  if (!children) return null;

  return <p className={cn("text-14", state === "error" ? "text-brand-red" : "text-neutral-grey")}>{children}</p>;
};
